import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaBookOpen,
  FaExclamationTriangle,
  FaHome,
  FaImage,
  FaLayerGroup,
} from "react-icons/fa";

import {
  getMenuPagesPersonnalisees,
  getPagePersonnaliseeBySlug,
} from "../../../services/pagesPersonnaliseesServices";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const getImageUrl = (url) => {
  if (!url) return "";

  const cleanUrl = String(url || "").trim();

  if (!cleanUrl) return "";

  if (
    isExternalURL(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  const cleanBaseUrl = BASE_URL.replace(/\/$/, "");

  if (cleanUrl.startsWith("/")) {
    return `${cleanBaseUrl}${cleanUrl}`;
  }

  return `${cleanBaseUrl}/${cleanUrl}`;
};

const getArticles = (page) => {
  if (Array.isArray(page?.articles)) return page.articles;
  if (Array.isArray(page?.Articles)) return page.Articles;
  return [];
};

const sortArticles = (articles = []) => {
  return [...articles].sort((a, b) => {
    const orderDiff = Number(a.ordre || 0) - Number(b.ordre || 0);
    if (orderDiff !== 0) return orderDiff;
    return Number(a.id || 0) - Number(b.id || 0);
  });
};

function PageSkeleton() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="h-72 animate-pulse bg-slate-100 dark:bg-slate-800" />
        <div className="p-6">
          <div className="h-8 w-72 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-800" />
          <div className="mt-5 h-24 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800" />
        </div>
      </div>

      <div className="mt-6 grid gap-5">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className="h-48 animate-pulse rounded-[2rem] bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    </div>
  );
}

function EmptyState({ title, message }) {
  return (
    <div className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6 lg:px-8">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-300">
        <FaExclamationTriangle className="text-2xl" />
      </div>

      <h1 className="mt-6 text-2xl font-bold text-slate-950 dark:text-white">
        {title}
      </h1>

      <p className="mt-3 text-sm leading-7 text-slate-500 dark:text-slate-400">
        {message}
      </p>

      <Link
        to="/"
        className="mt-8 inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/20 transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
      >
        <FaHome />
        Retour à l’accueil
      </Link>
    </div>
  );
}

function ArticleBlock({ article, index }) {
  const imageUrl = getImageUrl(article.url_image);
  const hasImage = Boolean(imageUrl);
  const reverse = index % 2 === 1;

  return (
    <article className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm shadow-slate-200/70 dark:border-slate-800 dark:bg-slate-900 dark:shadow-none">
      <div
        className={`grid gap-0 ${
          hasImage ? "lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.15fr)]" : ""
        }`}
      >
        {hasImage && (
          <div
            className={`relative min-h-[260px] bg-slate-100 dark:bg-slate-800 ${
              reverse ? "lg:order-2" : ""
            }`}
          >
            <img
              src={imageUrl}
              alt={article.image_alt || article.titre_article || "Illustration"}
              className="h-full min-h-[260px] w-full object-cover"
              loading="lazy"
            />

            <div className="absolute left-4 top-4 rounded-full bg-slate-950/80 px-3 py-1 text-xs font-bold text-white backdrop-blur">
              Article {index + 1}
            </div>
          </div>
        )}

        <div className="flex flex-col justify-center p-6 sm:p-8 lg:p-10">
          <div className="mb-4 inline-flex w-fit items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-primary dark:bg-orange-500/10 dark:text-orange-300">
            <FaBookOpen />
            Page du club
          </div>

          {article.titre_article && (
            <h2 className="text-2xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-3xl">
              {article.titre_article}
            </h2>
          )}

          {article.contenu_texte ? (
            <p className="mt-5 whitespace-pre-line text-base leading-8 text-slate-600 dark:text-slate-300">
              {article.contenu_texte}
            </p>
          ) : (
            <p className="mt-5 text-sm text-slate-400">
              Aucun contenu renseigné pour cet article.
            </p>
          )}
        </div>
      </div>
    </article>
  );
}

export default function PagePersonnaliseePublic() {
  const { slug } = useParams();

  const [page, setPage] = useState(null);
  const [menuPages, setMenuPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const articles = useMemo(() => sortArticles(getArticles(page)), [page]);

  const otherPages = useMemo(() => {
    return menuPages
      .filter((item) => item.slug !== slug)
      .slice(0, 6);
  }, [menuPages, slug]);

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true);
      setError("");

      try {
        const [pageData, menuData] = await Promise.all([
          getPagePersonnaliseeBySlug(slug),
          getMenuPagesPersonnalisees(),
        ]);

        if (!pageData) {
          setError("Page introuvable ou inactive.");
          setPage(null);
          return;
        }

        setPage(pageData);
        setMenuPages(Array.isArray(menuData) ? menuData : []);

        document.title =
          pageData.meta_title ||
          pageData.titre ||
          "Page du club";
      } catch (loadError) {
        console.error("Erreur lors du chargement de la page :", loadError);
        setError("Impossible de charger cette page.");
        setPage(null);
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, [slug]);

  if (loading) return <PageSkeleton />;

  if (error || !page) {
    return (
      <EmptyState
        title="Page non disponible"
        message={
          error ||
          "Cette page personnalisée n’existe pas, n’est plus active ou a été supprimée."
        }
      />
    );
  }

  return (
    <div className="bg-slate-50 dark:bg-slate-950">
      <section className="relative overflow-hidden border-b border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <div className="absolute -right-28 -top-28 h-80 w-80 rounded-full bg-primary/10 blur-3xl dark:bg-orange-500/10" />
        <div className="absolute -bottom-32 left-1/4 h-80 w-80 rounded-full bg-cyan-400/10 blur-3xl" />

        <div className="relative mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaArrowLeft />
              Accueil
            </Link>

            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-bold text-primary dark:bg-orange-500/10 dark:text-orange-300">
              <FaLayerGroup />
              Page personnalisée
            </span>
          </div>

          <div className="max-w-4xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-primary dark:text-orange-300">
              Informations du club
            </p>

            <h1 className="mt-4 text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl">
              {page.titre}
            </h1>

            {page.introduction && (
              <p className="mt-6 whitespace-pre-line text-lg leading-8 text-slate-600 dark:text-slate-300">
                {page.introduction}
              </p>
            )}
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        {articles.length ? (
          <div className="space-y-7">
            {articles.map((article, index) => (
              <ArticleBlock
                key={article.id || `${article.titre_article}-${index}`}
                article={article}
                index={index}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-[2rem] border border-dashed border-slate-300 bg-white p-10 text-center dark:border-slate-700 dark:bg-slate-900">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              <FaImage className="text-2xl" />
            </div>

            <h2 className="mt-5 text-xl font-bold text-slate-950 dark:text-white">
              Aucun article pour cette page
            </h2>

            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Vous pouvez ajouter du contenu depuis le tableau de bord.
            </p>
          </div>
        )}

        {otherPages.length > 0 && (
          <section className="mt-12 rounded-[2rem] border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.2em] text-primary dark:text-orange-300">
                  À découvrir aussi
                </p>
                <h2 className="mt-2 text-2xl font-bold text-slate-950 dark:text-white">
                  Autres pages du club
                </h2>
              </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {otherPages.map((item) => (
                <Link
                  key={item.id || item.slug}
                  to={`/pages/${item.slug}`}
                  className="group rounded-3xl border border-slate-200 bg-slate-50 p-5 transition hover:-translate-y-1 hover:border-primary/30 hover:bg-white hover:shadow-lg dark:border-slate-700 dark:bg-slate-950 dark:hover:border-orange-500/30 dark:hover:bg-slate-900"
                >
                  <div className="flex items-start gap-3">
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-white text-primary shadow-sm dark:bg-slate-900 dark:text-orange-300">
                      <FaBookOpen />
                    </span>

                    <div className="min-w-0">
                      <h3 className="line-clamp-2 font-bold text-slate-950 group-hover:text-primary dark:text-white dark:group-hover:text-orange-300">
                        {item.titre}
                      </h3>

                      {item.introduction && (
                        <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                          {item.introduction}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}